/**
 * @file Shared Calendar route helpers.
 */

const CALENDAR_DAY_ROUTE_PREFIX = "day";
const DATE_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function isValidDateKey(value) {
    const match = DATE_KEY_PATTERN.exec(String(value || ""));
    if (!match) {return false;}

    const year = Number(match[1]);
    const monthIndex = Number(match[2]) - 1;
    const day = Number(match[3]);
    const date = new Date(year, monthIndex, day);
    return date.getFullYear() === year &&
        date.getMonth() === monthIndex &&
        date.getDate() === day;
}

/**
 * Normalizes a Calendar subview route value.
 * @param {string|null} subView Calendar subview value.
 * @returns {string|null} Canonical Calendar subview, or null for month view.
 */
export function normalizeCalendarSubView(subView) {
    const rawValue = String(subView || "").trim();
    if (!rawValue) {return null;}

    const segments = rawValue.split("/").filter(Boolean);
    if (
        segments.length === 2 &&
        segments[0] === CALENDAR_DAY_ROUTE_PREFIX &&
        isValidDateKey(segments[1])
    ) {
        return `${CALENDAR_DAY_ROUTE_PREFIX}/${segments[1]}`;
    }

    return null;
}

/**
 * Returns the agenda date key for a Calendar day subview.
 * @param {string|null} subView Calendar subview value.
 * @returns {string|null} Date key, or null when the subview is month view.
 */
export function getCalendarDayDateKey(subView) {
    const normalized = normalizeCalendarSubView(subView);
    if (!normalized) {return null;}
    return normalized.split("/")[1] || null;
}

/**
 * Builds a canonical Calendar path.
 * @param {string|null} subView Optional Calendar subview.
 * @returns {string} Canonical Calendar path.
 */
export function buildCalendarPath(subView = null) {
    const normalizedSubView = normalizeCalendarSubView(subView);
    return normalizedSubView ? `/calendar/${normalizedSubView}` : "/calendar";
}

/**
 * Parses a canonical Calendar path.
 * @param {string} pathname URL pathname.
 * @returns {object|null} Parsed Calendar route data, or null.
 */
export function parseCalendarPath(pathname) {
    if (!pathname) {return null;}

    const segments = pathname.split("/").filter(Boolean);
    if (segments[0] !== "calendar") {return null;}

    if (segments.length === 1) {
        return {
            view: "calendar",
            subView: null,
            pathname: "/calendar",
        };
    }

    if (segments.length === 3 && segments[1] === CALENDAR_DAY_ROUTE_PREFIX) {
        const subView = normalizeCalendarSubView(`${segments[1]}/${segments[2]}`);
        if (!subView) {return null;}
        return {
            view: "calendar",
            subView,
            pathname: buildCalendarPath(subView),
        };
    }

    return null;
}

/**
 * Returns whether the pathname is in the Calendar route namespace.
 * @param {string} pathname URL pathname.
 * @returns {boolean} True when the path starts with `/calendar`.
 */
export function isCalendarLikePath(pathname) {
    if (!pathname) {return false;}
    return pathname.split("/").filter(Boolean)[0] === "calendar";
}

/**
 * Parses a pathname into a Calendar route candidate.
 * @param {string} pathname URL pathname.
 * @returns {{status: "valid", target: object}|{status: "invalid"}|null}
 */
export function parseCalendarRouteCandidate(pathname) {
    const target = parseCalendarPath(pathname);
    if (target) {
        return { status: "valid", target };
    }

    if (isCalendarLikePath(pathname)) {
        return { status: "invalid" };
    }

    return null;
}
